import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';

// Theme Types
export type ThemeMode = 'light' | 'dark' | 'system';
export type ActualTheme = 'light' | 'dark';

export interface ThemeColors {
  // Primary Colors
  primary: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    950: string;
  };

  // Secondary Colors
  secondary: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    950: string;
  };

  // Success Colors
  success: {
    50: string;
    100: string;
    500: string;
    600: string;
    700: string;
    900: string;
  };

  // Warning Colors
  warning: {
    50: string;
    100: string;
    500: string;
    600: string;
    700: string;
    900: string;
  };

  // Error Colors
  error: {
    50: string;
    100: string;
    500: string;
    600: string;
    700: string;
    900: string;
  };

  // Info Colors
  info: {
    50: string;
    100: string;
    500: string;
    600: string;
    700: string;
    900: string;
  };

  // Neutral Colors
  gray: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    950: string;
  };

  // Background Colors
  background: {
    primary: string;
    secondary: string;
    tertiary: string;
    elevated: string;
    overlay: string;
  };

  // Text Colors
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    inverse: string;
    disabled: string;
  };

  // Border Colors
  border: {
    primary: string;
    secondary: string;
    focus: string;
    error: string;
    success: string;
  };
}

export interface ThemePreferences {
  mode: ThemeMode;
  fontSize: 'sm' | 'md' | 'lg' | 'xl';
  animations: boolean;
  reducedMotion: boolean;
  highContrast: boolean;
  customColors?: Partial<ThemeColors>;
}

export interface ThemeContextType {
  // Current Theme State
  mode: ThemeMode;
  actualTheme: ActualTheme;
  colors: ThemeColors;
  preferences: ThemePreferences;

  // Theme Actions
  setTheme: (mode: ThemeMode) => void;
  toggleTheme: () => void;
  setFontSize: (size: ThemePreferences['fontSize']) => void;
  setAnimations: (enabled: boolean) => void;
  setReducedMotion: (enabled: boolean) => void;
  setHighContrast: (enabled: boolean) => void;
  resetPreferences: () => void;

  // Utility Functions
  isDark: boolean;
  isLight: boolean;
  getColor: (path: string) => string;
  generateColorVariants: (baseColor: string) => any;
}

// Default Theme Colors
const lightColors: ThemeColors = {
  primary: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
    950: '#172554',
  },
  secondary: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    900: '#14532d',
  },
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    900: '#78350f',
  },
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    900: '#7f1d1d',
  },
  info: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    500: '#0ea5e9',
    600: '#0284c7',
    700: '#0369a1',
    900: '#0c4a6e',
  },
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
    950: '#030712',
  },
  background: {
    primary: '#ffffff',
    secondary: '#f9fafb',
    tertiary: '#f3f4f6',
    elevated: '#ffffff',
    overlay: 'rgba(0, 0, 0, 0.5)',
  },
  text: {
    primary: '#111827',
    secondary: '#4b5563',
    tertiary: '#9ca3af',
    inverse: '#ffffff',
    disabled: '#d1d5db',
  },
  border: {
    primary: '#e5e7eb',
    secondary: '#d1d5db',
    focus: '#3b82f6',
    error: '#ef4444',
    success: '#22c55e',
  },
};

const darkColors: ThemeColors = {
  primary: {
    50: '#172554',
    100: '#1e3a8a',
    200: '#1e40af',
    300: '#1d4ed8',
    400: '#2563eb',
    500: '#3b82f6',
    600: '#60a5fa',
    700: '#93c5fd',
    800: '#bfdbfe',
    900: '#dbeafe',
    950: '#eff6ff',
  },
  secondary: {
    50: '#020617',
    100: '#0f172a',
    200: '#1e293b',
    300: '#334155',
    400: '#475569',
    500: '#64748b',
    600: '#94a3b8',
    700: '#cbd5e1',
    800: '#e2e8f0',
    900: '#f1f5f9',
    950: '#f8fafc',
  },
  success: {
    50: '#14532d',
    100: '#15803d',
    500: '#22c55e',
    600: '#16a34a',
    700: '#dcfce7',
    900: '#f0fdf4',
  },
  warning: {
    50: '#78350f',
    100: '#b45309',
    500: '#f59e0b',
    600: '#d97706',
    700: '#fef3c7',
    900: '#fffbeb',
  },
  error: {
    50: '#7f1d1d',
    100: '#b91c1c',
    500: '#ef4444',
    600: '#dc2626',
    700: '#fee2e2',
    900: '#fef2f2',
  },
  info: {
    50: '#0c4a6e',
    100: '#0369a1',
    500: '#0ea5e9',
    600: '#0284c7',
    700: '#e0f2fe',
    900: '#f0f9ff',
  },
  gray: {
    50: '#030712',
    100: '#111827',
    200: '#1f2937',
    300: '#374151',
    400: '#4b5563',
    500: '#6b7280',
    600: '#9ca3af',
    700: '#d1d5db',
    800: '#e5e7eb',
    900: '#f3f4f6',
    950: '#f9fafb',
  },
  background: {
    primary: '#0f172a',
    secondary: '#1e293b',
    tertiary: '#334155',
    elevated: '#1e293b',
    overlay: 'rgba(0, 0, 0, 0.8)',
  },
  text: {
    primary: '#f8fafc',
    secondary: '#cbd5e1',
    tertiary: '#94a3b8',
    inverse: '#0f172a',
    disabled: '#475569',
  },
  border: {
    primary: '#334155',
    secondary: '#475569',
    focus: '#60a5fa',
    error: '#ef4444',
    success: '#22c55e',
  },
};

// Default Preferences
const defaultPreferences: ThemePreferences = {
  mode: 'system',
  fontSize: 'md',
  animations: true,
  reducedMotion: false,
  highContrast: false,
};

// Create Context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Local Storage Keys
const STORAGE_KEYS = {
  THEME_MODE: 'class-tech-theme-mode',
  THEME_PREFERENCES: 'class-tech-theme-preferences',
};

// Utility Functions
const getSystemTheme = (): ActualTheme => {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return 'light';
};

const getStoredMode = (): ThemeMode => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEYS.THEME_MODE);
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      return stored as ThemeMode;
    }
  }
  return 'system';
};

const getStoredPreferences = (): ThemePreferences => {
  if (typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.THEME_PREFERENCES);
      if (stored) {
        return { ...defaultPreferences, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.warn('Failed to parse stored theme preferences:', error);
    }
  }
  return defaultPreferences;
};

// Theme Provider Component
interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(getStoredMode);
  const [preferences, setPreferencesState] =
    useState<ThemePreferences>(getStoredPreferences);
  const [systemTheme, setSystemTheme] = useState<ActualTheme>(getSystemTheme);

  // Calculate actual theme
  const actualTheme: ActualTheme = mode === 'system' ? systemTheme : mode;
  const colors = actualTheme === 'dark' ? darkColors : lightColors;

  // Apply custom colors if available
  const finalColors = useMemo(
    () =>
      preferences.customColors
        ? { ...colors, ...preferences.customColors }
        : colors,
    [colors, preferences.customColors]
  );

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    // Remove existing theme classes
    root.classList.remove('light', 'dark');

    // Add current theme class
    root.classList.add(actualTheme);

    // Apply font size
    root.classList.remove('text-sm', 'text-md', 'text-lg', 'text-xl');
    root.classList.add(`text-${preferences.fontSize}`);

    // Apply animation preferences
    if (!preferences.animations || preferences.reducedMotion) {
      root.classList.add('motion-reduce');
    } else {
      root.classList.remove('motion-reduce');
    }

    // Apply high contrast
    if (preferences.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }

    // Set CSS custom properties for colors
    Object.entries(finalColors).forEach(([category, colorSet]) => {
      if (typeof colorSet === 'object' && colorSet !== null) {
        Object.entries(colorSet).forEach(([shade, value]) => {
          if (typeof value === 'string') {
            root.style.setProperty(`--color-${category}-${shade}`, value);
          }
        });
      }
    });
  }, [actualTheme, preferences, finalColors]);

  // Theme Actions
  const setTheme = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    localStorage.setItem(STORAGE_KEYS.THEME_MODE, newMode);
  }, []);

  const toggleTheme = useCallback(() => {
    if (mode === 'system') {
      setTheme(systemTheme === 'dark' ? 'light' : 'dark');
    } else if (mode === 'light') {
      setTheme('dark');
    } else {
      setTheme('light');
    }
  }, [mode, setTheme, systemTheme]);

  const updatePreferences = useCallback((updates: Partial<ThemePreferences>) => {
    const newPreferences = { ...preferences, ...updates };
    setPreferencesState(newPreferences);
    localStorage.setItem(
      STORAGE_KEYS.THEME_PREFERENCES,
      JSON.stringify(newPreferences)
    );
  }, [preferences]);

  const setFontSize = useCallback((size: ThemePreferences['fontSize']) => {
    updatePreferences({ fontSize: size });
  }, [updatePreferences]);

  const setAnimations = useCallback((enabled: boolean) => {
    updatePreferences({ animations: enabled });
  }, [updatePreferences]);

  const setReducedMotion = useCallback((enabled: boolean) => {
    updatePreferences({ reducedMotion: enabled });
  }, [updatePreferences]);

  const setHighContrast = useCallback((enabled: boolean) => {
    updatePreferences({ highContrast: enabled });
  }, [updatePreferences]);

  const resetPreferences = useCallback(() => {
    setPreferencesState(defaultPreferences);
    localStorage.removeItem(STORAGE_KEYS.THEME_PREFERENCES);
    setTheme('system');
  }, [setTheme]);

  // Utility Functions
  const getColor = useCallback((path: string): string => {
    const pathArray = path.split('.');
    let current: any = finalColors;

    for (const key of pathArray) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return '#000000'; // fallback color
      }
    }

    return typeof current === 'string' ? current : '#000000';
  }, [finalColors]);

  const generateColorVariants = useCallback((baseColor: string) => {
    // This would generate color variants from a base color
    // For now, returning a simple implementation
    return {
      50: `${baseColor}10`,
      100: `${baseColor}20`,
      200: `${baseColor}30`,
      300: `${baseColor}40`,
      400: `${baseColor}50`,
      500: baseColor,
      600: `${baseColor}60`,
      700: `${baseColor}70`,
      800: `${baseColor}80`,
      900: `${baseColor}90`,
      950: `${baseColor}95`,
    };
  }, []);

  const contextValue: ThemeContextType = useMemo(() => ({
    // Current Theme State
    mode,
    actualTheme,
    colors: finalColors,
    preferences,

    // Theme Actions
    setTheme,
    toggleTheme,
    setFontSize,
    setAnimations,
    setReducedMotion,
    setHighContrast,
    resetPreferences,

    // Utility Functions
    isDark: actualTheme === 'dark',
    isLight: actualTheme === 'light',
    getColor,
    generateColorVariants,
  }), [
    actualTheme,
    finalColors,
    generateColorVariants,
    getColor,
    mode,
    preferences,
    resetPreferences,
    setAnimations,
    setFontSize,
    setHighContrast,
    setReducedMotion,
    setTheme,
    toggleTheme,
  ]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom Hook
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme Hook for Components
export const useThemeClasses = () => {
  const { isDark, preferences } = useTheme();

  return {
    isDark,
    isLight: !isDark,
    text: {
      primary: isDark ? 'text-white' : 'text-gray-900',
      secondary: isDark ? 'text-gray-300' : 'text-gray-600',
      tertiary: isDark ? 'text-gray-400' : 'text-gray-500',
    },
    bg: {
      primary: isDark ? 'bg-gray-900' : 'bg-white',
      secondary: isDark ? 'bg-gray-800' : 'bg-gray-50',
      tertiary: isDark ? 'bg-gray-700' : 'bg-gray-100',
    },
    border: {
      primary: isDark ? 'border-gray-700' : 'border-gray-200',
      secondary: isDark ? 'border-gray-600' : 'border-gray-300',
    },
    fontSize: `text-${preferences.fontSize}`,
    animations: preferences.animations ? 'transition-all duration-300' : '',
    reducedMotion: preferences.reducedMotion ? 'motion-reduce' : '',
  };
};
